/* eslint-disable no-underscore-dangle */
import { Dispatch, SetStateAction, useCallback, useEffect } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalNode } from 'lexical';

import {
  $isFileNode,
  $isImagesGalleryNode,
  $isMentionNode,
  $isSingleImageNode,
} from '../../nodes';

interface TrackMediaIdsAndMentionsPluginProps {
  setMediaIds: Dispatch<SetStateAction<string[]>>;
  setMentionsData: Dispatch<SetStateAction<{ id: string; type: string }[]>>;
}

export const TrackMediaIdsAndMentionsPlugin = ({
  setMediaIds,
  setMentionsData,
}: TrackMediaIdsAndMentionsPluginProps) => {
  const [editor] = useLexicalComposerContext();

  const getAllMediaIds = useCallback(() => {
    return editor.getEditorState().read(() => {
      const nodes = editor.getEditorState()._nodeMap;
      const ids: string[] = [];

      nodes.forEach((node: LexicalNode | null | undefined) => {
        if ($isSingleImageNode(node)) {
          ids.push(node.getId());
        }
        if ($isImagesGalleryNode(node)) {
          const galleryNodeIds = node.getIds();
          galleryNodeIds.forEach((id) => {
            ids.push(id);
          });
        }
        if ($isFileNode(node)) {
          ids.push(node.getId());
        }
      });

      return ids;
    });
  }, [editor]);

  const getAllMentionsData = useCallback(() => {
    return editor.getEditorState().read(() => {
      const nodes = editor.getEditorState()._nodeMap;
      const ids: { id: string; type: string }[] = [];

      nodes.forEach((node: LexicalNode | null | undefined) => {
        if ($isMentionNode(node)) {
          const mentionsData = node.getMentionData();

          if (mentionsData.id && mentionsData.type) {
            ids.push(mentionsData);
          }
        }
      });

      return ids;
    });
  }, [editor]);

  useEffect(() => {
    const unsubscribe = editor.registerUpdateListener(() => {
      const newIds = getAllMediaIds();
      const mentionsData = getAllMentionsData();

      setMediaIds((prevIds) => {
        if (JSON.stringify(prevIds) === JSON.stringify(newIds)) {
          return prevIds;
        }
        return newIds;
      });

      setMentionsData((prevMentionsData) => {
        if (JSON.stringify(prevMentionsData) === JSON.stringify(mentionsData)) {
          return prevMentionsData;
        }
        return mentionsData;
      });
    });

    return () => unsubscribe();
  }, [
    editor,
    getAllMediaIds,
    getAllMentionsData,
    setMediaIds,
    setMentionsData,
  ]);

  return null;
};
